/**
 * The @api registry — every backend call goes through the KORM contract at
 * /api/crud/:Model. Add named entries for ergonomics; `crud` is the generic door.
 */
const stripModel = ({ model, ...rest }) => rest;

// Every GitHub call is a custom action on one model, so the URL is fixed.
const GITHUB = '/api/crud/GithubConnection';
const ghAction = (action, data) => ({ url: GITHUB, method: 'POST', data: data ? { action, data } : { action } });

export default {
  login: (data) => ({ url: '/api/crud/User', method: 'POST', data: { action: 'login', data } }),
  logout: () => ({ url: '/api/crud/User', method: 'POST', data: { action: 'logout' } }),

  // Generic: fetchApi('crud', { model:'Bill', action:'list', where:{...}, data:{...} })
  crud: (body) => ({ url: `/api/crud/${body.model}`, method: 'POST', data: stripModel(body) }),

  // The two video endpoints that can't be korm actions: one signs an upload URL, the
  // other signs a playback URL and must stay reachable without a session. See
  // routes/video.js. The PUT to Spaces itself is NOT here on purpose — it must not carry
  // this app's baseURL or its credentials (see lib/videoUpload.js).
  videoUploadUrl: ({ id }) => ({ url: `/api/video/${id}/upload-url`, method: 'POST' }),
  videoPlayback: ({ publicId }) => ({ url: `/api/video/${publicId}/playback`, method: 'GET' }),
  // Embed.jsx does NOT use this — it runs in a cross-site frame and needs
  // credentials:'include' with a plain fetch. This entry is for in-app callers only.
  videoUnlock: ({ publicId, passcode }) => ({ url: `/api/video/${publicId}/unlock`, method: 'POST', data: { passcode } }),

  // Utility Tools — DB Migration (schema/DDL diff + apply). These hit the bespoke
  // routers under /api/utility/db-migration/*, NOT the generic /api/crud door: they
  // talk to EXTERNAL databases supplied by URL, which korm knows nothing about.
  dbMigrationTest: (data) => ({ url: '/api/utility/db-migration/test-connection', method: 'POST', data }),
  dbMigrationTables: (data) => ({ url: '/api/utility/db-migration/tables', method: 'POST', data }),
  dbMigrationPlan: (data) => ({ url: '/api/utility/db-migration/plan', method: 'POST', data }),
  dbMigrationRuns: () => ({ url: '/api/utility/db-migration/runs', method: 'GET' }),
  dbMigrationRun: ({ id }) => ({ url: `/api/utility/db-migration/runs/${id}`, method: 'GET' }),
  dbMigrationExecuteStep: ({ runId, stepId }) => ({ url: `/api/utility/db-migration/runs/${runId}/steps/${stepId}/execute`, method: 'POST' }),
  dbMigrationClear: () => ({ url: '/api/utility/db-migration/runs', method: 'DELETE' }),

  // Utility Tools — Data Migration (row data copy/sync between two external DBs).
  dataMigrationTest: (data) => ({ url: '/api/utility/data-migration/test-connection', method: 'POST', data }),
  dataMigrationTables: (data) => ({ url: '/api/utility/data-migration/tables', method: 'POST', data }),
  dataMigrationPlan: (data) => ({ url: '/api/utility/data-migration/plan', method: 'POST', data }),
  dataMigrationRuns: () => ({ url: '/api/utility/data-migration/runs', method: 'GET' }),
  dataMigrationRun: ({ id }) => ({ url: `/api/utility/data-migration/runs/${id}`, method: 'GET' }),
  dataMigrationExecuteStep: ({ runId, stepId }) => ({ url: `/api/utility/data-migration/runs/${runId}/steps/${stepId}/execute`, method: 'POST' }),
  dataMigrationSkipStep: ({ runId, stepId }) => ({ url: `/api/utility/data-migration/runs/${runId}/steps/${stepId}/skip`, method: 'POST' }),
  // Clears finished (completed/partial/failed) runs; deleteRun removes one by id.
  // Local audit rows only — the source/target databases are never touched.
  dataMigrationClear: () => ({ url: '/api/utility/data-migration/runs', method: 'DELETE' }),
  dataMigrationDeleteRun: ({ id }) => ({ url: `/api/utility/data-migration/runs/${id}`, method: 'DELETE' }),

  // Per-table SOURCE→TARGET row diff + reconcile. Query params are baked into the
  // URL because the registry passes a `data` body only, which a GET cannot carry.
  dataMigrationDiff: ({ runId, table, page = 1, limit = 50, which = 'source' }) => ({
    url: `/api/utility/data-migration/runs/${runId}/tables/${encodeURIComponent(table)}/diff?${new URLSearchParams({ page, limit, which })}`,
    method: 'GET',
  }),
  // pk null → POST (insert: {data} or {fromPk}); pk set → PUT (update: {data, where?}).
  dataMigrationRowUpsert: ({ runId, table, pk = null, data, where, fromPk }) => {
    const base = `/api/utility/data-migration/runs/${runId}/tables/${encodeURIComponent(table)}/rows`;
    return pk == null
      ? { url: base, method: 'POST', data: { data, fromPk } }
      : { url: `${base}/${encodeURIComponent(pk)}`, method: 'PUT', data: { data, where } };
  },
  dataMigrationRowDelete: ({ runId, table, pk, where }) => ({
    url: `/api/utility/data-migration/runs/${runId}/tables/${encodeURIComponent(table)}/rows/${encodeURIComponent(pk)}`,
    method: 'DELETE',
    data: { where },
  }),
  dataMigrationRowSync: ({ runId, table, pk, where }) => ({
    url: `/api/utility/data-migration/runs/${runId}/tables/${encodeURIComponent(table)}/rows/${encodeURIComponent(pk)}/sync`,
    method: 'POST',
    data: { where },
  }),

  // GitHub — the whole connect flow rides custom actions on the korm door, so every
  // step is gated by BaseModel.validate rather than by a bespoke router.
  github: (body) => ({ url: GITHUB, method: 'POST', data: body }),
  githubStatus: () => ghAction('status'),
  githubConnectStart: () => ghAction('connectStart'),
  githubConnectCallback: (data) => ghAction('connectCallback', data),
  githubListRepos: () => ghAction('listRepos'),
  githubListBranches: ({ repo }) => ghAction('listBranches', { repo }),
  githubSetRepo: ({ repo, branch }) => ghAction('setRepo', { repo, branch }),
  githubDisconnect: () => ghAction('disconnect'),

  // Development — GitApp custom actions. `where:{id}` targets the app; `data` carries
  // the action's own arguments, matching what the model reads.
  gitApps: (body) => ({ url: '/api/crud/GitApp', method: 'POST', data: body }),
  gitAppAction: (action, id, data) => ({
    url: '/api/crud/GitApp',
    method: 'POST',
    data: { action, where: { id }, data: data || {} },
  }),
  gitAppTickets: (body) => ({ url: '/api/crud/GitAppTicket', method: 'POST', data: body }),
};
